import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Mail,
  CheckCircle2,
  AlertTriangle,
  Eye,
  Send,
  Loader2,
  X,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { API } from '@/config/api';

/**
 * Reliability Digest settings card + preview modal + test-send.
 *
 * The card lets the owner:
 *  - enable / disable the monthly email digest
 *  - manage the recipient list
 *  - preview the next email body before it's sent
 *  - fire a one-off test email
 *
 * If the backend reports `email_provider_configured: false` (no Resend
 * key), the test button still works but the result is rendered as
 * "queued" — never fake success.
 */
export default function ReliabilityDigestSettingsCard() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newRecipient, setNewRecipient] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [history, setHistory] = useState([]);

  const authHeaders = useCallback(() => {
    const token =
      localStorage.getItem('backoffice_token') ||
      localStorage.getItem('merchant_token') ||
      localStorage.getItem('access_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([
        axios.get(`${API}/owner/kiosks/reliability/digest-settings`, {
          headers: authHeaders(),
        }),
        axios
          .get(`${API}/owner/kiosks/reliability/digest-history?limit=10`, {
            headers: authHeaders(),
          })
          .catch(() => ({ data: { history: [] } })),
      ]);
      setSettings(s.data);
      setHistory(h.data?.history || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unable to load digest settings');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const patch = useCallback(
    async (body) => {
      setSaving(true);
      try {
        const res = await axios.patch(
          `${API}/owner/kiosks/reliability/digest-settings`,
          body,
          { headers: authHeaders() },
        );
        setSettings((prev) => ({ ...res.data, email_provider_configured: prev?.email_provider_configured }));
        toast.success('Saved');
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Save failed');
      } finally {
        setSaving(false);
      }
    },
    [authHeaders],
  );

  const toggleEnabled = (next) => {
    if (next && !(settings?.recipients?.length > 0)) {
      toast.error('Add at least one recipient first.');
      return;
    }
    patch({ enabled: next });
  };

  const addRecipient = () => {
    const r = newRecipient.trim().toLowerCase();
    if (!r || !r.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    const next = Array.from(new Set([...(settings?.recipients || []), r]));
    setNewRecipient('');
    patch({ recipients: next });
  };

  const removeRecipient = (email) => {
    const next = (settings?.recipients || []).filter((r) => r !== email);
    patch({ recipients: next });
  };

  const openPreview = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const res = await axios.get(
        `${API}/owner/kiosks/reliability/digest-preview`,
        { headers: authHeaders() },
      );
      setPreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const sendTest = async () => {
    const target = settings?.recipients?.[0];
    if (!target) {
      toast.error('Add a recipient first.');
      return;
    }
    setTesting(true);
    try {
      const res = await axios.post(
        `${API}/owner/kiosks/reliability/digest/send-test`,
        { recipient: target },
        { headers: authHeaders() },
      );
      if (res.data?.status === 'sent') {
        toast.success(`Sent test to ${target}`);
      } else if (res.data?.status === 'queued') {
        toast.warning(
          `Queued — email provider not configured (Resend API key missing). The audit row is recorded.`,
        );
      } else {
        toast.error(res.data?.error || 'Send failed');
      }
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="reliability-digest-card">
        <CardContent className="py-6 text-center text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
          Loading digest settings…
        </CardContent>
      </Card>
    );
  }

  const recipients = settings?.recipients || [];
  const providerOk = settings?.email_provider_configured;

  return (
    <Card data-testid="reliability-digest-card" className="border-slate-200">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Mail className="h-4 w-4 text-slate-500" />
            Monthly Reliability Digest
          </CardTitle>
          <div className="flex items-center gap-2">
            {providerOk ? (
              <Badge
                variant="outline"
                className="bg-emerald-50 text-emerald-700 border-emerald-200"
              >
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Email provider ready
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="bg-amber-50 text-amber-800 border-amber-200"
                data-testid="reliability-digest-provider-warning"
              >
                <AlertTriangle className="h-3 w-3 mr-1" />
                Email provider not configured (sends will queue)
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 pt-2">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Send monthly digest</p>
            <p className="text-xs text-slate-500">
              Runs on the 1st of each month at 06:00 UTC.
            </p>
          </div>
          <Switch
            checked={!!settings?.enabled}
            onCheckedChange={toggleEnabled}
            disabled={saving}
            data-testid="reliability-digest-enabled-switch"
          />
        </div>

        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">
            Recipients
          </p>
          {recipients.length === 0 ? (
            <p className="text-sm text-slate-400 italic mb-2">
              No recipients yet.
            </p>
          ) : (
            <div className="space-y-1 mb-2">
              {recipients.map((email) => (
                <div
                  key={email}
                  className="flex items-center justify-between bg-slate-50 px-3 py-1.5 rounded-md"
                  data-testid={`reliability-digest-recipient-${email}`}
                >
                  <span className="text-sm">{email}</span>
                  <button
                    type="button"
                    onClick={() => removeRecipient(email)}
                    className="text-slate-400 hover:text-rose-600"
                    aria-label={`Remove ${email}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Input
              placeholder="owner@chain29.com"
              value={newRecipient}
              onChange={(e) => setNewRecipient(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addRecipient()}
              className="flex-1"
              data-testid="reliability-digest-recipient-input"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={addRecipient}
              disabled={saving}
              data-testid="reliability-digest-add-recipient"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
          <Button
            variant="outline"
            size="sm"
            onClick={openPreview}
            data-testid="reliability-digest-preview-button"
          >
            <Eye className="h-4 w-4 mr-2" />
            Preview email
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={sendTest}
            disabled={testing || recipients.length === 0}
            data-testid="reliability-digest-send-test-button"
          >
            {testing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Send test
          </Button>
        </div>

        {settings?.last_sent_at && (
          <p className="text-[11px] text-slate-500">
            Last delivery: {new Date(settings.last_sent_at).toLocaleString()} ·{' '}
            <span
              className={
                settings.last_send_status === 'sent'
                  ? 'text-emerald-700'
                  : settings.last_send_status === 'queued'
                  ? 'text-amber-700'
                  : 'text-rose-700'
              }
            >
              {settings.last_send_status}
            </span>
          </p>
        )}

        {history.length > 0 && (
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide mt-2 mb-2">
              Recent activity
            </p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {history.map((h) => (
                <div
                  key={h.id}
                  className="flex items-center justify-between text-xs px-2 py-1 rounded hover:bg-slate-50"
                >
                  <span className="truncate flex-1">
                    {h.recipient}
                    {h.is_test ? ' (test)' : ''}
                  </span>
                  <Badge
                    variant="outline"
                    className={
                      h.status === 'sent'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : h.status === 'queued'
                        ? 'bg-amber-50 text-amber-800 border-amber-200'
                        : 'bg-rose-50 text-rose-700 border-rose-200'
                    }
                  >
                    {h.status}
                  </Badge>
                  <span className="text-[10px] text-slate-400 ml-2">
                    {new Date(h.created_at).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      {previewOpen && (
        <PreviewModal
          loading={previewLoading}
          preview={preview}
          onClose={() => {
            setPreviewOpen(false);
            setPreview(null);
          }}
        />
      )}
    </Card>
  );
}

function PreviewModal({ preview, loading, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="reliability-digest-preview-modal"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide">
              Email preview
            </p>
            <p className="text-sm font-semibold truncate">
              {preview?.subject || 'Loading…'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
            aria-label="Close preview"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto bg-slate-50 p-4">
          {loading || !preview ? (
            <div className="text-center text-sm text-slate-500 py-10">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
              Rendering preview…
            </div>
          ) : (
            <iframe
              title="Digest preview"
              srcDoc={preview.html}
              className="w-full h-[70vh] bg-white rounded shadow-sm border border-slate-200"
            />
          )}
        </div>
      </div>
    </div>
  );
}
